// USDA RELEASE CALENDAR — exact, sourced dates for the reports SILAS watches.
//
// Kevin, 2026-10-20, on Home's "On the calendar" card ("Milk Production Oct
// 20–Oct 24 window"): "This calendar should not show date ranges; we should know
// the exact date reports are scheduled for, they must be posted somewhere."
// They are. Both publishers post the exact day and time, and this module is the
// ONE place SILAS reads them:
//
//   NASS — the Reports-by-Date calendar, scraped daily by api/_usda-calendar.js
//          into its run log (sync_runs.details). Each day cell carries a time heading
//          ("3:00 pm ET") followed by calendar-landing links whose own query
//          string names year / month / day. NASS posts only as far ahead as it
//          has published: measured 2026-10-20, Oct–Dec 2026 carry a calendar and
//          every 2027 month carries NO calendar table at all. Past that horizon a
//          report's date is "not yet posted by NASS" — a named absence, never a
//          range and never a guess.
//   AMS  — Class & component price release dates, from AMS's own two-year PDFs
//          (RDFMO-0007 for 2025-26, RDFMO-0008 for 2027-28). Transcribed below and
//          re-verified by tools/ams-class-release-dates.js, which re-parses the
//          PDFs and diffs them against this table. These are NOT "the 5th": 2026
//          runs Apr 1, Apr 29, Jun 3 … Sep 30, Nov 4 — some land inside the price
//          month itself.
//
// CommonJS on purpose: required by api/_home.js and api/_usda-calendar.js, and
// imported by src/App.jsx (the DRP "not priced yet" line).
"use strict";

const NASS_CALENDAR_URL = (year, month) =>
  "https://www.nass.usda.gov/Publications/Calendar/reports_by_date.php?view=c&month=" +
  String(month).padStart(2, "0") + "&year=" + year;

const AMS_PDF = {
  "2025-2026": "https://www.ams.usda.gov/sites/default/files/media/2025-2026AdvancedandClassPriceReleaseDates.pdf",
  "2027-2028": "https://www.ams.usda.gov/sites/default/files/media/2027_2028AdvancedandClassPriceReleaseDates.pdf",
};
// "the class and component prices will be released at 3:00 p.m. ET" — both PDFs.
const AMS_RELEASE_TIME_ET = "3:00 pm";

// [month the prices apply to, release date] — the "Class and Component Prices"
// release column of RDFMO-0007 (2025-26) and RDFMO-0008 (2027-28).
const AMS_CLASS_PRICE_RELEASES = [
  ["2025-01", "2025-02-05"],
  ["2025-02", "2025-03-05"],
  ["2025-03", "2025-04-02"],
  ["2025-04", "2025-04-30"],
  ["2025-05", "2025-06-04"],
  ["2025-06", "2025-07-02"],
  ["2025-07", "2025-07-30"],
  ["2025-08", "2025-09-04"],
  ["2025-09", "2025-10-01"],
  ["2025-10", "2025-11-05"],
  ["2025-11", "2025-12-03"],
  ["2025-12", "2025-12-31"],
  ["2026-01", "2026-02-04"],
  ["2026-02", "2026-03-04"],
  ["2026-03", "2026-04-01"],
  ["2026-04", "2026-04-29"],
  ["2026-05", "2026-06-03"],
  ["2026-06", "2026-07-01"],
  ["2026-07", "2026-08-05"],
  ["2026-08", "2026-09-02"],
  ["2026-09", "2026-09-30"],
  ["2026-10", "2026-11-04"],
  ["2026-11", "2026-12-02"],
  ["2026-12", "2026-12-30"],
  ["2027-01", "2027-02-03"],
  ["2027-02", "2027-03-03"],
  ["2027-03", "2027-03-31"],
  ["2027-04", "2027-05-05"],
  ["2027-05", "2027-06-03"],
  ["2027-06", "2027-06-30"],
  ["2027-07", "2027-08-04"],
  ["2027-08", "2027-09-01"],
  ["2027-09", "2027-09-29"],
  ["2027-10", "2027-11-03"],
  ["2027-11", "2027-12-01"],
  ["2027-12", "2028-01-05"],
  ["2028-01", "2028-02-02"],
  ["2028-02", "2028-03-01"],
  ["2028-03", "2028-04-05"],
  ["2028-04", "2028-05-03"],
  ["2028-05", "2028-06-01"],
  ["2028-06", "2028-06-28"],
  ["2028-07", "2028-08-02"],
  ["2028-08", "2028-08-30"],
  ["2028-09", "2028-10-04"],
  ["2028-10", "2028-11-01"],
  ["2028-11", "2028-11-29"],
  ["2028-12", "2029-01-04"],
];

// The reports Home's "On the calendar" card carries. `nassName` is the link text
// on NASS's calendar, matched EXACTLY — "Crop Production Annual" is a different
// report and must not stand in for the monthly one.
const CALENDAR_REPORTS = [
  { id: "crop_progress",   label: "Crop Progress",            agency: "NASS", nassName: "Crop Progress" },
  { id: "crop_production", label: "Crop Production",          agency: "NASS", nassName: "Crop Production" },
  { id: "milk_production", label: "Milk Production",          agency: "NASS", nassName: "Milk Production" },
  { id: "cattle_on_feed",  label: "Cattle on Feed",           agency: "NASS", nassName: "Cattle on Feed" },
  { id: "ag_prices",       label: "Agricultural Prices",      agency: "NASS", nassName: "Agricultural Prices" },
  { id: "class_prices",    label: "Class & component prices", agency: "AMS" },
];
const AMS_REPORT_NAME = "Class & component prices";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");

function amsSourceUrl(releaseIso) {
  const y = Number(String(releaseIso).slice(0, 4));
  return y <= 2026 ? AMS_PDF["2025-2026"] : AMS_PDF["2027-2028"];
}

// The scheduled AMS release for one month's Class & component prices, or null
// when that month is outside the posted schedule. Never a computed "5th".
function amsClassReleaseFor(priceYear, priceMonth) {
  const key = Number(priceYear) + "-" + pad(Number(priceMonth));
  const hit = AMS_CLASS_PRICE_RELEASES.find((r) => r[0] === key);
  if (!hit) return null;
  return { priceMonth: key, date: hit[1], timeET: AMS_RELEASE_TIME_ET, sourceUrl: amsSourceUrl(hit[1]) };
}

// The sentence a surface prints for a month whose Class prices are not out yet:
// the EXACT scheduled AMS release, or — past the posted schedule — a named
// absence. Never "on or before the 5th" and never a computed day.
function amsClassReleaseText(priceYear, priceMonth) {
  const r = amsClassReleaseFor(priceYear, priceMonth);
  return r ? "AMS release scheduled " + fmtReleaseDate(r.date) + ", " + r.timeET + " ET"
           : "AMS release date not yet posted";
}

// The next scheduled AMS Class & component price release on or after `todayIso`
// (an ET date), from the posted schedule — never a computed "5th". Null past it.
function nextAmsClassRelease(todayIso) {
  const t = String(todayIso || "").slice(0, 10);
  const hit = AMS_CLASS_PRICE_RELEASES.find((r) => r[1] >= t);
  return hit ? { priceMonth: hit[0], date: hit[1], timeET: AMS_RELEASE_TIME_ET, sourceUrl: amsSourceUrl(hit[1]) } : null;
}
const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
// "Next USDA Class prices: Nov 4, 3:00 pm ET (October prices)"
function nextAmsClassReleaseText(todayIso) {
  const r = nextAmsClassRelease(todayIso);
  if (!r) return "Next USDA Class prices: date not yet posted by AMS";
  const m = r.date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return "Next USDA Class prices: " + MON[Number(m[2]) - 1] + " " + Number(m[3]) + ", " + r.timeET + " ET ("
    + MONTH_LONG[Number(r.priceMonth.slice(5, 7)) - 1] + " prices)";
}

// "2026-11-04" -> "Nov 4, 2026"
function fmtReleaseDate(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return MON[Number(m[2]) - 1] + " " + Number(m[3]) + ", " + m[1];
}

// AMS rows in the same shape as the scraped NASS rows.
function amsReleaseRows() {
  return AMS_CLASS_PRICE_RELEASES.map((r) => ({
    report_name: AMS_REPORT_NAME, agency: "AMS", release_date: r[1],
    release_time_et: AMS_RELEASE_TIME_ET, source_url: amsSourceUrl(r[1]), price_month: r[0],
  }));
}

const decode = (s) => String(s).replace(/&amp;/g, "&").replace(/&nbsp;/g, " ")
  .replace(/&#0?39;|&apos;/g, "'").replace(/&quot;/g, '"').trim();

// One month of NASS's Reports-by-Date calendar -> rows. The DATE comes from each
// link's own year/month/day parameters, not from the cell's printed day; the
// TIME is the last "H:MM pm ET" heading above the link inside the SAME day cell,
// and resets at every cell. `cells` counts calendar day cells: a month NASS has
// not posted has none, which is how "not yet posted" is told apart from a parser
// that stopped matching (cells present, rows zero).
function parseNassCalendarHtml(html, year, month) {
  const rows = [];
  const parts = String(html || "").split(/<td\b/i);
  let cells = 0;
  for (const part of parts.slice(1)) {
    if (!/^[^>]*class="calendar/i.test(part)) continue;
    cells++;
    let time = null;
    const re = /(\d{1,2}:\d{2}\s*[ap]\.?m\.?)\s*ET|calendar-landing\.php\?([^"]*)"\s*>([^<]*)<\/a>/gi;
    let m;
    while ((m = re.exec(part))) {
      if (m[1]) { time = m[1].toLowerCase().replace(/\./g, "").replace(/\s+/, " "); continue; }
      const qs = decode(m[2]);
      const y = (qs.match(/(?:^|&)year=(\d{2,4})/) || [])[1];
      const mo = (qs.match(/(?:^|&)month=(\d{1,2})/) || [])[1];
      const d = (qs.match(/(?:^|&)day=(\d{1,2})/) || [])[1];
      if (!y || !mo || !d) continue;
      const yy = y.length === 2 ? 2000 + Number(y) : Number(y);
      if (year != null && (yy !== Number(year) || Number(mo) !== Number(month))) continue;
      rows.push({ report_name: decode(m[3]), release_date: yy + "-" + pad(mo) + "-" + pad(d),
                  release_time_et: time, agency: "NASS", source_url: NASS_CALENDAR_URL(yy, Number(mo)) });
    }
  }
  return { rows, cells };
}

const isoDiffDays = (a, b) => Math.round((Date.parse(b + "T00:00:00Z") - Date.parse(a + "T00:00:00Z")) / 86400000);

// Today's date in Eastern time — the clock both publishers release on.
function etDateIso(now) {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" })
    .formatToParts(now instanceof Date ? now : new Date(now));
  const g = (t) => (p.find((x) => x.type === t) || {}).value;
  return g("year") + "-" + g("month") + "-" + g("day");
}

// The next scheduled release of every CALENDAR_REPORTS entry on or after
// `todayIso` (an ET date). `nassRows` are the scraped rows (latest usda-calendar run);
// `nassLoaded` is false when that read failed. A report with no posted date is
// returned with exact: null and a PUBLIC reason — it is never given a range.
function buildReleaseCalendar(todayIso, nassRows, { nassLoaded = true } = {}) {
  const rows = (nassRows || []).filter((r) => r && r.agency === "NASS").concat(amsReleaseRows());
  const out = [];
  for (const rep of CALENDAR_REPORTS) {
    const name = rep.agency === "AMS" ? AMS_REPORT_NAME : rep.nassName;
    const next = rows
      .filter((r) => r.agency === rep.agency && r.report_name === name && String(r.release_date).slice(0, 10) >= todayIso)
      .sort((a, b) => (a.release_date < b.release_date ? -1 : a.release_date > b.release_date ? 1 : 0))[0];
    if (next) {
      const exact = String(next.release_date).slice(0, 10);
      out.push({ id: rep.id, label: rep.label, agency: rep.agency, exact, daysAway: isoDiffDays(todayIso, exact),
                 timeET: next.release_time_et || null, posted: true,
                 source: rep.agency + " release calendar", sourceUrl: next.source_url || null });
    } else {
      const reason = rep.agency === "NASS" && !nassLoaded
        ? "NASS release date not available right now"
        : "date not yet posted by " + rep.agency;
      out.push({ id: rep.id, label: rep.label, agency: rep.agency, exact: null, daysAway: null, timeET: null,
                 posted: false, absentReason: reason, source: rep.agency + " release calendar", sourceUrl: null });
    }
  }
  out.sort((a, b) => (a.posted !== b.posted ? (a.posted ? -1 : 1)
    : ((a.daysAway == null ? 1e9 : a.daysAway) - (b.daysAway == null ? 1e9 : b.daysAway)) || a.label.localeCompare(b.label)));
  return out;
}

module.exports = {
  NASS_CALENDAR_URL, AMS_PDF, AMS_RELEASE_TIME_ET, AMS_CLASS_PRICE_RELEASES, AMS_REPORT_NAME, CALENDAR_REPORTS,
  amsClassReleaseFor, amsClassReleaseText, amsReleaseRows, fmtReleaseDate, parseNassCalendarHtml, buildReleaseCalendar, etDateIso,
  nextAmsClassRelease, nextAmsClassReleaseText,
};
